import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { navTo } from "../components/ContextChain";

/**
 * Shell content-search (Cat 47) — workspace-level page. Queries the REAL backend
 * GET /api/workspace/search?q=…&workspace_id=… and renders typed results;
 * engagement results jump to their home. Distinct from the Command Palette
 * (nav/command search) — this searches DATA. No stubs; honest empty-states.
 *
 * Engagement results carry a backend-DERIVED `client_id` (spec 20, data-attachment arc 1),
 * so deep-links use the real client — no ContextChain fallback, no fabricated default. An
 * unplaced engagement (`client_id` null) opens the workspace home rather than guess a client.
 */
export interface SearchResult {
  type: "engagement" | "library";
  id: string;
  label: string;
  workspace_id?: string | null;
  client_id?: string | null;
}

export interface SearchPageProps {
  apiBase?: string;
  fetchImpl?: typeof fetch;
}

type ViewState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; results: SearchResult[] };

export function SearchPage({ apiBase = "/api/workspace", fetchImpl }: SearchPageProps): React.ReactElement {
  const { ws } = useParams({ strict: false }) as { ws?: string };
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [state, setState] = useState<ViewState>({ kind: "idle" });

  const runSearch = async () => {
    const q = query.trim();
    if (!q) {
      setState({ kind: "idle" });
      return;
    }
    setState({ kind: "loading" });
    const f = fetchImpl ?? fetch;
    const base = apiBase.replace(/\/$/, "");
    const wsq = ws ? `&workspace_id=${encodeURIComponent(ws)}` : "";
    try {
      const res = await f(`${base}/search?q=${encodeURIComponent(q)}${wsq}`, { method: "GET" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const results = (await res.json()) as SearchResult[];
      setState({ kind: "ready", results });
    } catch (e) {
      setState({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  };

  const openResult = (r: SearchResult) => {
    if (r.type !== "engagement" || !ws) return;
    if (r.client_id) {
      navigate(navTo.engagementHome(ws, r.client_id, r.id) as never);
    } else {
      // Honest null-client handling (spec 20 D-A5): an unplaced engagement has no client →
      // open the workspace home rather than fabricate a default client.
      navigate(navTo.workspaceHome(ws) as never);
    }
  };

  return (
    <div data-app="search">
      <div className="relative p-2">
        <Search size={14} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)]" aria-hidden />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && runSearch()}
          placeholder="Search engagements, library…"
          aria-label="Search query"
          className="w-full rounded-[2px] border border-[var(--color-border-default)] bg-[var(--color-surface-base)] py-2 pl-8 pr-2 text-sm text-[var(--color-text-primary)] outline-none placeholder:text-[var(--color-text-secondary)] focus:border-[var(--color-accent)]"
        />
      </div>

      {state.kind === "idle" && (
        <p className="px-3 pb-2 text-xs text-[var(--color-text-secondary)]">Press Enter to search.</p>
      )}
      {state.kind === "loading" && (
        <div role="status" className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">Searching…</div>
      )}
      {state.kind === "error" && (
        <div role="alert" className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">Couldn’t load results: {state.message}</div>
      )}
      {state.kind === "ready" &&
        (state.results.length === 0 ? (
          <div className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">No results for “{query}”.</div>
        ) : (
          <ul className="flex flex-col">
            {state.results.map((r) => {
              // Only engagement results have a navigation destination today (openResult).
              // Library results are not yet openable — render them non-interactive rather than
              // as a button that silently no-ops (a dead clickable row reads as broken).
              const actionable = r.type === "engagement";
              return (
                <li key={`${r.type}-${r.id}`}>
                  {actionable ? (
                    <button
                      type="button"
                      onClick={() => openResult(r)}
                      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-surface-muted)]"
                    >
                      <span className="truncate">{r.label}</span>
                      <span className="shrink-0 text-xs text-[var(--color-text-secondary)]">{r.type}</span>
                    </button>
                  ) : (
                    <div
                      className="flex w-full cursor-default items-center justify-between gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-secondary)]"
                      title="Not yet openable"
                    >
                      <span className="truncate">{r.label}</span>
                      <span className="shrink-0 text-xs italic text-[var(--color-text-secondary)]">{r.type} · not yet openable</span>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ))}
    </div>
  );
}
